import { Box, Typography } from "@mui/material";
import ChatList from "./ChatList";
import ContactDetailWindow from "./ContactDetailWindow";
import CustomTabs from "../common/custom-tabs";
import { ChatProvider } from "../hooks/ChatProvider";
import InstagramLiveChat from "./InstagramFlow/InstagramLiveChat";
import { useInstagramApi } from "./InstagramFlow/shared";
import { INSTAGRAM_ACCOUNTS } from "../config/urls";
import { useEffect, useState } from "react";

// Values match the backend's channel param exactly (chat/consumers.py's
// SupportChatQueueConsumer) — "chats" and "calls" filter the same live
// contact list, "all" shows every contact once regardless of which
// channel(s) they've used.
const LEFT_PANEL_TABS = [
  { label: "All", value: "all" },
  { label: "Chat", value: "chats" },
  { label: "Calls", value: "calls" },
];

export default function ChatHome() {
  const [isChatSelected, setIsChatSelected] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const { request, notify, toast } = useInstagramApi();
  const [igAccounts, setIgAccounts] = useState([]); // connected Instagram accounts

  useEffect(() => {
    request("get", INSTAGRAM_ACCOUNTS)
      .then((res) => setIgAccounts(res.accounts.filter((a) => a.status !== "DISCONNECTED")))
      .catch(() => setIgAccounts([])); // no access or not set up: just no Instagram tab
  }, [request]);

  // The Instagram tab is a separate inbox, shown only when an account is connected.
  const tab = activeTab === "instagram" && !igAccounts.length ? "all" : activeTab;
  const instagram = tab === "instagram";
  const tabs = igAccounts.length ? [...LEFT_PANEL_TABS, { label: "Instagram", value: "instagram" }] : LEFT_PANEL_TABS;

  const listHeader = (
    <>
      {/* Sidebar Header */}
      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          bgcolor: "#1a2340",
          display: "flex",
          alignItems: "center",
          minHeight: 64,
        }}
      >
        <Typography
          sx={{
            color: "#ffffff",
            fontWeight: 700,
            fontSize: "1.1rem",
            letterSpacing: "0.3px",
          }}
        >
          Chats
        </Typography>
      </Box>

      {/* Chat / Calls / All tab switcher — above the list's own search bar */}
      <Box sx={{ px: 1.5, py: 1, borderBottom: "1px solid #e8e8e8" }}>
        <CustomTabs
          value={tab}
          onChange={(event, value) => setActiveTab(value)}
          tabs={tabs}
          // 4 tabs in the 300px column: size each tab to its label so "Instagram" isn't clipped
          tabSx={igAccounts.length ? { px: 1, flex: "1 1 auto", width: "auto" } : undefined}
        />
      </Box>
    </>
  );

  return (
    <ChatProvider>
      <Box
        sx={{
          height: "calc(100vh - 80px)", // adjust to your app's header height
          display: "flex",
          flexDirection: "column",
          bgcolor: "#f0f2f5",
          px: { xs: 0, md: 2 },
          py: { xs: 0, md: 2 },
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            minHeight: 0,
            borderRadius: { xs: 0, md: "16px" },
            overflow: "hidden",
            boxShadow: { xs: "none", md: "0 4px 24px rgba(0,0,0,0.08)" },
            border: { xs: "none", md: "1px solid #e0e0e0" },
          }}
        >
          {instagram ? (
            <InstagramLiveChat
              accounts={igAccounts}
              request={request}
              notify={notify}
              listHeader={listHeader}
              sx={{ flex: 1, height: "auto", minHeight: 0, border: 0, borderRadius: 0 }}
            />
          ) : (
            <>
              {/* Sidebar – unified contact list, filtered by the active tab */}
              <Box
                sx={{
                  width: { xs: "100%", md: "300px", lg: "320px" },
                  flexShrink: 0,
                  display: {
                    xs: isChatSelected ? "none" : "flex",
                    md: "flex",
                  },
                  flexDirection: "column",
                  borderRight: { xs: "none", md: "1px solid #e8e8e8" },
                  bgcolor: "#ffffff",
                }}
              >
                {listHeader}
                <ChatList setIsChatSelected={setIsChatSelected} channel={tab} />
              </Box>

              {/* Main – selected contact's Chat/Calls sub-tabs */}
              <Box
                sx={{
                  flex: 1,
                  display: {
                    xs: isChatSelected ? "flex" : "none",
                    md: "flex",
                  },
                  flexDirection: "column",
                  minWidth: 0,
                }}
              >
                <ContactDetailWindow setIsChatSelected={setIsChatSelected} activeTab={tab} />
              </Box>
            </>
          )}
        </Box>
      </Box>
      {toast}
    </ChatProvider>
  );
}
